import { type MouseEvent, useEffect, useState } from 'react'
import { Dialog, DialogContent, Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { PlusIcon, UserIcon } from '@dhl-ui/icons'
import { DialogTitleClosable } from '@dhl/components'
import { Box, Button, Divider, H5, MenuItem, Paper } from '@dhl-ui/components'
import { UsersCard as CommonUsersCard } from '~/common/components'
import { AddUserForm, ExistingUsers } from '~/modules/organisations/components'
import { useOrganisationUsers } from '~/modules/organisations/hooks'

type Props = {
  organisationId: string
}

export const UsersCard = ({ organisationId }: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const onOpen = () => setOpen(true)
  const onClose = () => setOpen(false)
  const [refreshData, setRefreshData] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const { organisationUsers, isFetching, refetch } = useOrganisationUsers(organisationId)

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const addExistingUsers = () => {
    setIsVisible(true)
  }

  const handleClose = () => {
    setIsVisible(false)
  }

  const onRefreshData = (value: boolean) => {
    setRefreshData(value)
    setAnchorEl(null)
  }

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false)
      refetch()
    }
  }, [refreshData])

  return (
    <>
      <CommonUsersCard
        organisationId={organisationId}
        isLoading={isFetching}
        users={organisationUsers}
        action={
          <>
            <Button
              data-testid="add-user"
              variant="contained"
              color="primary"
              size="small"
              aria-controls="customized-menu"
              aria-haspopup="true"
              startIcon={<PlusIcon />}
              onClick={handleClick}
            >
              {t('admin.organisationsPage.users.addUserButton')}
            </Button>

            <Menu
              elevation={2}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              id="customized-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem data-testid="create-user" onClick={onOpen} icon={<PlusIcon />}>
                {t('admin.organisationsPage.userForm.createUser')}
              </MenuItem>
              <MenuItem data-testid="existing-user" onClick={() => addExistingUsers()} icon={<UserIcon />}>
                {t('admin.organisationsPage.userForm.addExisting')}
              </MenuItem>
            </Menu>
          </>
        }
      />
      {organisationId && (
        <AddUserForm open={open} onClose={onClose} organisationId={organisationId} onRefreshData={onRefreshData} />
      )}

      <Dialog data-testid="existing-user-form" open={isVisible} onClose={handleClose} fullWidth>
        <Box>
          <DialogTitleClosable onClose={handleClose}>
            <Box alignSelf="left">
              <H5>{t('admin.organisationsPage.users.existingUserButton')}</H5>
            </Box>
          </DialogTitleClosable>
        </Box>
        <Divider />
        <DialogContent sx={{ minHeight: 720 }}>
          {organisationId && (
            <ExistingUsers organisationId={organisationId} onClose={handleClose} onRefreshData={onRefreshData} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
